import React, { useState, useEffect, useCallback } from "react"
import { devices } from "../../ajax_clients/devices"
import { Skeleton } from "@mui/material"
import ComplianceOverrideModal from "./compliance_override_modal"
import { flashError } from "../../shared/functions"

const { readCompliance, refreshCompliance } = devices

interface ComplianceMeta {
  label: string
  className: string
}

interface ComplianceAttributes {
  inScope?: boolean
  complianceState?: string | null
  overrideActive?: boolean
  lastEvaluation?: string | null
  complianceOverride?: string | null
  overrideExpiresAt?: string | null
  gracePeriodExpiresAt?: string | null
  deactivatedAt?: string | null
  enrolled?: boolean
}

interface ComplianceResponse {
  data?: {
    attributes?: ComplianceAttributes
  }
  meta?: {
    policy?: {
      update?: boolean
    }
  }
}

interface ComplianceOverrideProps {
  deviceId: number
}

const COMPLIANCE_META: Record<string, ComplianceMeta | undefined> = {
  none: { label: "None", className: "label-default" },
  compliant: { label: "Compliant", className: "label-success" },
  notRegistered: {
    label: "Awaiting Entra registration",
    className: "label-warning",
  },
  deactivated: { label: "Deactivated", className: "label-default" },
  override_compliant: { label: "Compliant", className: "label-success" },
  notCompliant: { label: "Not compliant", className: "label-danger" },
  override_notCompliant: { label: "Not compliant", className: "label-danger" },
}

interface BootstrapBadgeProps {
  label: string | null
  className?: string
  active?: boolean
  timestamp?: string | null
  timestampLabel?: string
}

function BootstrapBadge({
  label,
  className = "label-default",
  active = true,
  timestamp,
  timestampLabel,
}: BootstrapBadgeProps) {
  const formattedTimestamp = timestamp
    ? new Date(timestamp).toLocaleString()
    : null
  const suffix = [timestampLabel, formattedTimestamp].filter(Boolean).join(" ")

  let suffixText = ""
  if (active && suffix) {
    suffixText = label ? ` (${suffix})` : suffix
  }

  return (
    <span className={`label ${className}`}>
      {label}
      {suffixText}
    </span>
  )
}

export default function ComplianceOverride({
  deviceId,
}: ComplianceOverrideProps) {
  const [complianceData, setComplianceData] =
    useState<ComplianceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  // On mount, read the current compliance state for the device
  useEffect(() => {
    setLoading(true)
    readCompliance(deviceId)
      .then((data: ComplianceResponse) => {
        setComplianceData(data)
        setLoadError(false)
        setLoading(false)
      })
      .catch(({ responseJSON }: JQuery.jqXHR) => {
        flashError(responseJSON, "Failed to load compliance state.")
        setLoadError(true)
        setLoading(false)
      })
  }, [deviceId])

  const {
    inScope,
    complianceState,
    overrideActive,
    lastEvaluation,
    complianceOverride,
    overrideExpiresAt,
    gracePeriodExpiresAt,
    deactivatedAt,
    enrolled,
  }: ComplianceAttributes = complianceData?.data?.attributes || {}

  const canUpdateOverride = complianceData?.meta?.policy?.update ?? false
  const isDeactivated = Boolean(deactivatedAt)

  const { label: complianceLabel, className: complianceClassName } =
    COMPLIANCE_META[complianceState ?? ""] || {
      label: complianceState || "",
    }

  const { label: overrideLabel, className: overrideClassName } =
    COMPLIANCE_META[overrideActive ? (complianceOverride ?? "") : "none"] || {
      label: complianceOverride || "",
    }

  const lastEvalLabel = lastEvaluation
    ? new Date(lastEvaluation).toLocaleString()
    : "N/A"

  const handleRefresh = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      setLoading(true)
      refreshCompliance(deviceId)
        .then((data: ComplianceResponse) => {
          setComplianceData(data)
          setLoading(false)
        })
        .catch(({ responseJSON }: JQuery.jqXHR) => {
          flashError(responseJSON, "Failed to refresh compliance state.")
          setLoading(false)
        })
    },
    [deviceId],
  )

  if (loadError) {
    return (
      <>
        <h3 style={{ marginTop: "30px" }}>MS Intune Device Compliance</h3>
        <p>Compliance details are currently unavailable.</p>
      </>
    )
  }

  return (
    <>
      <h3 style={{ marginTop: "30px" }}>MS Intune Device Compliance</h3>

      <table className="key-value">
        <tbody>
          <tr>
            <td>In Scope</td>
            <td>
              {loading ? (
                <Skeleton variant="rectangular" width={40} height={20} />
              ) : inScope ? (
                "Yes"
              ) : (
                "No"
              )}
            </td>
          </tr>

          <tr>
            <td>Grace Period</td>
            <td>
              {loading ? (
                <Skeleton variant="rectangular" width={80} height={20} />
              ) : gracePeriodExpiresAt ? (
                <BootstrapBadge
                  label={null}
                  className="label-warning"
                  timestamp={gracePeriodExpiresAt}
                  timestampLabel="until"
                />
              ) : (
                "N/A"
              )}
            </td>
          </tr>

          <tr>
            <td>Compliance Status</td>
            <td>
              {loading ? (
                <Skeleton variant="rectangular" width={80} height={20} />
              ) : complianceState ? (
                <BootstrapBadge
                  label={complianceLabel}
                  className={complianceClassName}
                  timestamp={deactivatedAt}
                />
              ) : (
                "N/A"
              )}
            </td>
          </tr>

          <tr>
            <td>Compliance Override</td>
            <td>
              {loading ? (
                <Skeleton variant="rectangular" width={100} height={20} />
              ) : isDeactivated ? (
                "N/A"
              ) : (
                <>
                  <BootstrapBadge
                    label={overrideLabel}
                    className={overrideClassName}
                    active={overrideActive}
                    timestamp={overrideExpiresAt}
                    timestampLabel={
                      overrideExpiresAt ? "until" : "indefinitely"
                    }
                  />
                  {complianceState && canUpdateOverride ? (
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        setModalOpen(true)
                      }}
                      title="Edit compliance override"
                      style={{ marginLeft: "5px" }}
                    >
                      <span className="glyphicon glyphicon-pencil btn-xs"></span>
                    </a>
                  ) : null}
                </>
              )}
            </td>
          </tr>

          <tr>
            <td>Last Evaluated</td>
            <td>
              {loading ? (
                <Skeleton variant="rectangular" width={100} height={20} />
              ) : (
                <>
                  {lastEvalLabel}
                  {canUpdateOverride && enrolled && (
                    <a
                      href="#"
                      title="Refresh compliance check"
                      data-confirm="This will re-evaluate the device against the compliance group. Are you sure?"
                      onClick={handleRefresh}
                    >
                      <span className="glyphicon glyphicon-repeat btn-xs"></span>
                    </a>
                  )}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      {modalOpen && (
        <ComplianceOverrideModal
          onClose={() => setModalOpen(false)}
          onSave={(data: ComplianceResponse) => setComplianceData(data)}
          deviceId={deviceId}
          complianceOverride={complianceOverride}
          overrideExpiresAt={overrideExpiresAt}
        />
      )}
    </>
  )
}
